export default {
    seo: {
        title: 'Regula Data | No-Code Data Integration & Enterprise ETL Platform',
        description: 'We will help you eliminate manual data entry between systems. We offer integrations that automatically synchronize data across the platforms you use.',
        ogTitle: 'Integrate your software tools with Regula Data',
        ogDescription: 'We will help you eliminate manual data entry between systems. We offer integrations that automatically synchronize data across the platforms you use.',
        keywords: 'data integration, workflow automation, SaaS connectors, data synchronization, business process automation, enterprise data pipeline, custom software integration, rivile integration, begin integration, butenta integration, api integration, api'
    },
    header: {
        howItWorks: 'How It Works',
        pricing: 'Pricing',
        contact: 'Contact',
        blog: 'Blog',
        login: 'Log in'
    },
    hero: {
        badge: 'Data Integration',
        title: 'Connect your software solutions',
        subtitle: 'We will help you eliminate manual data entry between systems. We offer integrations that automatically synchronize data across the platforms you use.',
        cta: 'Get Started',
        ctaShort: 'Start',
        learnMore: 'Learn More',
        bullet1: 'Transparent pricing',
        bullet2: 'Extensive integration options',
        bullet3: 'Tailored to your business',
        dashboardAlt: 'Regula Data Dashboard'
    },
    features: {
        badge: 'Features',
        title: 'Everything you need for effective work',
        subtitle: 'Powerful data integration tools designed to ensure data reliability and security.',
        browse: {
            title: 'Easy setup',
            description: 'Choose from our extensive integration library. If the integration you need isn\'t there, we will build a custom one for your business.'
        },
        configure: {
            title: 'Quick configuration',
            description: 'Just enter your login details and pick your settings. We take care of all the technical heavy lifting behind the scenes.'
        },
        activate: {
            title: 'Automated process',
            description: 'Set your preferred integration schedule and enjoy automatic data synchronization. You will no longer need to manually copy data between systems'
        },
    },
    howItWorks: {
        badge: 'The Process',
        title: 'Three steps to a seamless system',
        subtitle: 'A streamlined process to quickly connect the tools you use every day.',
        step1: {
            title: 'Tell us your needs',
            description: 'Get in touch and let us know which systems you want to connect. We’ll discuss your data flow requirements and find the best solution for your business.'
        },
        step2: {
            title: 'We prepare your integration',
            description: 'If we have a ready-made integration in our library, we’ll help you configure it. If not, we’ll offer to build a custom integration tailored to your needs.'
        },
        step3: {
            title: 'Enjoy the results',
            description: 'Once your integration is live, data flows between systems automatically-no more manual data transfers between platforms.'
        },
        enterprise: {
            title: 'Highest reliability standards',
            description: 'Our integrations meet the most rigorous requirements to ensure uninterrupted data flow.',
            performance: {
                title: 'Custom-built solutions',
                description: 'Need a specific integration? Our team will build it precisely according to your business specifications.'
            },
            security: {
                title: 'Tested and production-ready',
                description: 'Every integration is thoroughly tested, continuously monitored, and regularly updated.'
            },
            support: {
                title: 'Continuous improvement',
                description: 'We actively update our platform based on client feedback, technological shifts, and changes in the systems we integrate.'
            },
            imageAlt: 'Business team collaboration'
        }
    },
    integrations: {
        badge: 'Integrations',
        title: 'Seamlessly connect your business ecosystem',
        subtitle: 'A growing library of integrations built around your needs.',
        categories: {
            crm: 'CRM',
            marketing: 'Marketing',
            analytics: 'Analytics',
            productivity: 'Productivity',
            ecommerce: 'E-Commerce',
            support: 'Support'
        },
        cards: {
            restApi: {
                title: 'REST API',
                description: 'We can connect any systems that provide an API interface.'
            },
            saas: {
                title: 'SaaS Platforms',
                description: 'Direct integrations between the business applications your team uses every day.'
            },
            webhooks: {
                title: 'Webhooks',
                description: 'Synchronize data in real time using webhook integrations.'
            }
        },
        custom: {
            title: 'Looking for a specific integration?',
            description: 'We specialize in building custom integrations on demand. Tell us your requirements and we’ll get to work.',
            cta: 'Request Custom Integration'
        }
    },
    pricing: {
        badge: 'Pricing',
        title: 'Simple, transparent pricing',
        subtitle: 'Choose a plan that fits your synchronization needs.',
        weekly: {
            title: 'Weekly Sync',
            price: '39',
            currency: '€',
            period: 'per integration/month',
            frequency: 'Sync up to once per week',
            cta: 'Get Started'
        },
        daily: {
            title: 'Daily Sync',
            price: '79',
            currency: '€',
            period: 'per integration/month',
            frequency: 'Sync up to once per day',
            badge: 'Most Popular',
            cta: 'Get Started'
        },
        hourly: {
            title: 'Hourly Sync',
            price: '129',
            currency: '€',
            period: 'per integration/month',
            frequency: 'Sync up to once per hour',
            cta: 'Get Started'
        },
        webhook: {
            title: 'Webhook Integration',
            price: 'Custom Pricing',
            period: 'tailored to your scale',
            frequency: 'Sync in real-time (event-triggered)',
            cta: 'Contact Us'
        },
        features: {
            setup: 'Quick and simple setup',
            monitoring: 'Real-time monitoring',
            support: 'Email support'
        },
        vatNote: 'excl. VAT',
        customNote: '* Custom integrations not currently in our library may require an additional one-time development fee.'
    },
    contact: {
        badge: 'Get in Touch',
        title: 'Looking to optimize your processes?',
        subtitle: 'Get in touch to discuss your integration needs.',
        form: {
            name: 'Full name',
            email: 'Email address',
            company: 'Company name',
            phone: 'Phone number',
            message: 'How can we help with your integration needs?',
            submit: 'Send message',
            sending: 'Sending...',
            namePlaceholder: 'Name Surname',
            emailPlaceholder: "name{'@'}company.com",
            companyPlaceholder: 'Your Company Inc.',
            phonePlaceholder: '+1 (555) 000-0000',
            privacyNotice: {
                before: 'By submitting this form, you agree to our',
                privacy: 'privacy policy',
                between: 'and',
                terms: 'terms of service',
                after: '.'
            }
        },
        success: 'Thank you! Your message has been received. We’ll be in touch shortly.',
        errors: {
            validation: 'Please ensure all fields are filled out correctly.',
            captcha: 'Please complete the security verification.',
            rateLimit: 'Too many attempts. Please try again in a few minutes.',
            generic: 'An error occurred. Please try again or contact us directly.'
        }
    },
    cta: {
        title: 'Ready to connect your tools?',
        subtitle: 'Join the companies using Regula Data to synchronize data across their systems.',
        getStarted: 'Get Started',
        contactSales: 'Contact Us',
        benefit1: 'Fast implementation',
        benefit2: 'Custom integration options',
        benefit3: 'Tailored to your business'
    },
    blog: {
        title: 'Blog',
        description: 'Latest news and insights from Regula Data',
        readMore: 'Read more',
        publishedOn: 'Published on',
        backToBlog: 'Back to blog',
        noPosts: 'No blog posts yet.',
        notFound: 'Post not found',
        notFoundDescription: 'The blog post you are looking for does not exist.',
        calculator: {
            title: 'Calculate your own cost',
            subtitle: 'Enter a single recurring task to see what it actually costs your business.',
            employeeCost: 'Employee cost',
            month: 'month',
            duration: 'Task duration',
            frequency: 'How often?',
            units: {
                min: 'min',
                hrs: 'hrs',
            },
            frequencies: {
                daily: 'Every working day',
                weekly: 'Once a week',
                monthly: 'Once a month',
            },
            results: {
                perMonth: 'per month',
                perYear: 'per year',
                hours: 'hours',
                or: 'or',
            },
            note: 'Effective hourly rate: {rate} (total employee cost ÷ 176 working hours/month)',
        },
    },
    footer: {
        copyright: '© 2026 Regula Data. All rights reserved.'
    },
    legal: {
        privacy: {
            title: 'Privacy Policy',
            lastUpdated: 'Last updated: February 4, 2026',
            introduction: {
                title: '1. Introduction',
                content: 'This Privacy Policy explains how Regula Data collects and processes personal information submitted through the contact form on our landing page. We are committed to protecting your privacy and handling your data responsibly.'
            },
            informationCollected: {
                title: '2. Information We Collect',
                content: 'When you submit our contact form, we collect the following information:',
                items: [
                    'Your full name',
                    'Your email address',
                    'Your phone number (if provided)',
                    'Your company name',
                    'The message you provide describing your integration needs'
                ]
            },
            howWeUse: {
                title: '3. How We Use Your Information',
                content: 'We use the information you provide to:',
                items: [
                    'Respond to your inquiry about our services',
                    'Communicate with you about your integration needs',
                    'Provide information about Regula Data solutions',
                    'Follow up on your request for custom integrations or services'
                ]
            },
            dataSharing: {
                title: '4. Data Sharing',
                content: 'We do not sell your personal information. Your contact form data may be shared with our email service provider for the purpose of delivering your inquiry to our team. We do not share your information with any other third parties without your consent.'
            },
            dataSecurity: {
                title: '5. Data Security',
                content: 'We implement standard security measures to protect your information from unauthorized access. Your data is transmitted securely and stored using industry-standard encryption practices. However, no method of transmission over the Internet is 100% secure.'
            },
            yourRights: {
                title: '6. Your Rights (GDPR)',
                content: 'Under the General Data Protection Regulation (GDPR), you have the right to:',
                items: [
                    'Access your personal data that we hold',
                    'Request correction of inaccurate information',
                    'Request deletion of your personal data',
                    'Object to processing of your personal data',
                    'Withdraw consent at any time'
                ]
            },
            cookies: {
                title: '7. Cookies',
                content: 'Our website uses cookies strictly for security and functionality purposes. These essential cookies help protect against spam and malicious traffic, and verify that visitors are human when using the contact form. We do not use cookies for advertising, analytics, or tracking.'
            },
            changes: {
                title: '8. Changes to This Policy',
                content: 'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated date. Your continued use of our website after changes indicates acceptance.'
            },
            contact: {
                title: '9. Contact Us',
                content: "If you have questions about this Privacy Policy or wish to exercise your data protection rights, please contact us at: info{'@'}reguladata.lt"
            }
        },
        terms: {
            title: 'Terms of Service',
            lastUpdated: 'Last updated: January 29, 2026',
            acceptance: {
                title: '1. Acceptance of Terms',
                content: 'By using the contact form on the Regula Data landing page, you agree to these Terms of Service. If you do not agree, please do not submit the form.'
            },
            services: {
                title: '2. Website and Contact Form',
                content: 'This website provides information about Regula Data integration services. The contact form allows you to submit inquiries about our services. We aim to respond to all inquiries promptly.'
            },
            obligations: {
                title: '3. User Obligations',
                content: 'When using our website and contact form, you agree to:',
                items: [
                    'Provide accurate and truthful information',
                    'Use the website only for lawful purposes',
                    'Not submit spam, malicious content, or false information',
                    'Not attempt to interfere with website operation'
                ]
            },
            intellectualProperty: {
                title: '4. Intellectual Property',
                content: 'All content on this website, including text, graphics, logos, and design, is the property of Regula Data and is protected by copyright and other intellectual property laws. You may not reproduce or distribute any content without permission.'
            },
            liability: {
                title: '5. Limitation of Liability',
                content: 'Regula Data is not liable for any damages arising from your use of the contact form or website. We make no warranties about the accuracy or completeness of information on this site. Your use of the form is at your own risk.'
            },
            termination: {
                title: '6. Access Termination',
                content: 'We reserve the right to restrict access to our website for misuse. This does not affect any data protection rights you may have.'
            },
            governingLaw: {
                title: '7. Governing Law',
                content: 'These Terms are governed by the laws of the Republic of Lithuania. Any disputes shall be resolved in the courts of Vilnius, Lithuania.'
            },
            changes: {
                title: '8. Changes to Terms',
                content: 'We may update these Terms at any time by posting revised terms on this page. Your continued use of the contact form after changes indicates acceptance of the updated Terms.'
            },
            contact: {
                title: '9. Contact Information',
                content: "If you have questions about these Terms, please contact us at: info{'@'}reguladata.lt"
            }
        }
    }
};